import { useCallback, useEffect, useMemo, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ModalContext } from "@/components/ModalContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ContactModal } from "@/components/modals/ContactModal";
import { ScheduleModal } from "@/components/modals/ScheduleModal";
import { PresupuestoModal } from "@/components/modals/PresupuestoModal";
import { Lightbox } from "@/components/Lightbox";
import Home from "@/pages/Home";
import Legal from "@/pages/Legal";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

function App() {
  const [contactOpen, setContactOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [presupuestoOpen, setPresupuestoOpen] = useState(false);
  const [contactPrefill, setContactPrefill] = useState("");

  const openContact = useCallback((prefill = "") => {
    setContactPrefill(prefill);
    setContactOpen(true);
  }, []);

  const openSchedule = useCallback(() => setScheduleOpen(true), []);

  const openPresupuesto = useCallback(() => setPresupuestoOpen(true), []);

  const modalApi = useMemo(
    () => ({ openContact, openSchedule, openPresupuesto }),
    [openContact, openSchedule, openPresupuesto]
  );

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf = requestAnimationFrame(function loop(t) {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    });
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  useEffect(() => {
    const lenis = window.__lenis;
    if (!lenis) return;
    if (contactOpen || scheduleOpen || presupuestoOpen) lenis.stop();
    else lenis.start();
  }, [contactOpen, scheduleOpen, presupuestoOpen]);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollToTop />
        <ModalContext.Provider value={modalApi}>
          <div
            aria-hidden
            className="noise-overlay pointer-events-none fixed inset-0 z-[70] opacity-[0.035] mix-blend-multiply"
          />
          <Toaster position="bottom-center" richColors />
          <Header />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/aviso-legal" element={<Legal />} />
            <Route path="/politica-de-privacidad" element={<Legal />} />
            <Route path="/politica-de-cookies" element={<Legal />} />
            <Route path="*" element={<Home />} />
          </Routes>
          <Footer />
          <ContactModal
            open={contactOpen}
            onOpenChange={setContactOpen}
            prefill={contactPrefill}
          />
          <ScheduleModal open={scheduleOpen} onOpenChange={setScheduleOpen} />
          <PresupuestoModal
            open={presupuestoOpen}
            onOpenChange={setPresupuestoOpen}
          />
        </ModalContext.Provider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
